import React from 'react';

import { Grid2 as Grid, styled } from '@mui/material';

import {
  ConversionTeaserSide,
  DownloadDefaultIcon,
  DownloadHoverIcon,
  IconTextButton,
  ResponsiveGrid,
} from '@mewa/ui';

import t from '../../../libs/translate';
import Image from '../../components/image';
import { AccordionData, DataAccordion } from './components/data-accordion';
import { ModelIntroText } from './components/model-intro-text';
import logoServiceThumbnailSrc from './logo-service-thumbnail.jpg';
import { Props, TeaserLink } from './types';

const teaserImageData = [
  {
    src: logoServiceThumbnailSrc,
    media: '',
    width: 408,
    height: 260,
  },
];

const DataSectionClothing = ({
  data,
  accordionData,
  contactLink,
  onBrochuresDownload,
  logoTeaser,
  conversionTeaserSide,
}: {
  data: Props;
  accordionData: AccordionData;
  contactLink: TeaserLink;
  onBrochuresDownload: () => void;
  logoTeaser: {
    headline: string;
    description: string;
  };
  conversionTeaserSide: {
    headline1: string;
    headline2: string;
    description: string;
    onClick: () => void;
  };
}) => {
  const DataSection = (
    <>
      <DataAccordion contactLink={contactLink} data={accordionData} />

      <BrochuresDownloadButtonContainer>
        <IconTextButton
          DefaultIcon={DownloadDefaultIcon}
          HoverIcon={DownloadHoverIcon}
          onClick={onBrochuresDownload}
        >
          {t(data.translations, 'general_download_brochure')}
        </IconTextButton>
      </BrochuresDownloadButtonContainer>
    </>
  );

  return (
    <>
      <ResponsiveGrid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <StyledModelIntroText
            headline={data.pimProduct.h1}
            body={data.pimProduct.infoText}
          />
          <SideConversion
            headline1={conversionTeaserSide.headline1}
            headline2={conversionTeaserSide.headline2}
            description={conversionTeaserSide.description}
            cta={{
              label: contactLink.buttonLabel,
              onClick: conversionTeaserSide.onClick,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ServiceTeaserGrid container columns={12} data-pagefind-ignore>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Image images={teaserImageData} alt={logoTeaser.headline} />
            </Grid>
            <LogoTeaserText size={{ xs: 12, md: 6 }}>
              <ServiceTeaserHeadline>
                {logoTeaser.headline}
              </ServiceTeaserHeadline>
              <ServiceTeaserParagraph>
                {logoTeaser.description}
              </ServiceTeaserParagraph>
            </LogoTeaserText>
          </ServiceTeaserGrid>
          <DataSectionFloatRight>{DataSection}</DataSectionFloatRight>
        </Grid>
      </ResponsiveGrid>
      <DataSectionTablet>
        <Grid size={12}>{DataSection}</Grid>
      </DataSectionTablet>
    </>
  );
};

export default DataSectionClothing;

const SideConversion = styled(ConversionTeaserSide)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    maxWidth: '500px',
  },
}));

const DataSectionFloatRight = styled('div')(({ theme }) => ({
  [theme.breakpoints.between('sm', 'md')]: {
    display: 'none',
  },
}));

const DataSectionTablet = styled(ResponsiveGrid)(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const LogoTeaserText = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.between('sm', 'md')]: {
    marginTop: '16px',
  },

  [theme.breakpoints.between('lg', 1700)]: {
    paddingRight: '30px',
  },
}));
const StyledModelIntroText = styled(ModelIntroText)(({ theme }) => ({
  marginTop: '44px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '100px',
  },
}));
const BrochuresDownloadButtonContainer = styled('div')({
  marginTop: '18px',
  marginLeft: '-12px',
});

const ServiceTeaserGrid = styled(Grid)(({ theme }) => ({
  marginTop: '60px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '100px',
  },
}));

const ServiceTeaserHeadline = styled('h4')(({ theme }) => ({
  ...theme.typography.h6,

  marginBottom: '8px',

  [theme.breakpoints.up('md')]: {
    marginTop: 0,
  },
}));

const ServiceTeaserParagraph = styled('p')(({ theme }) => ({
  ...theme.typography.body,
  margin: 0,
  maxWidth: '90%',
}));
